<template>
    <div class="flex flex-col gap-2">
        <div
            v-if="timelineItems.length > 0 && !navStore.isLoading"
            class="timeline flex flex-col gap-7"
            data-test="timeline-wrapper"
        >
            <NuxtLink
                v-for="(item) in timelineItems"
                :key="item.id"
                :href="item.linkUrl"
                target="_blank"
                rel="noopener"
                class="timeline-item bg-white w-full rounded-md px-6 py-5"
                data-test="timeline-item-link"
            >
                <timeline-item
                    :item="item"
                    data-test="timeline-item"
                />
            </NuxtLink>
            <timeline-content-loader
                class="loading-more"
                data-test="timeline-loading-more-loader"
                :visible="isLoadingMore"
            />
        </div>

        <shared-info-message v-if="timelineItems.length === 0 && timelineError">
            {{ timelineError }}
        </shared-info-message>

        <shared-info-message v-if="isSinglePost && !navStore.isLoading">
            <template #icon>
                <icon-menu-home
                    custom-color="#ffffff"
                    class="icon-extra-small mr-2.5 mb-1"
                />
            </template>
            <template #default>
                <a
                    class="to-all-posts flex items-center"
                    href="/"
                >
                    {{ $t('timeline.view_all_posts') }}
                </a>
            </template>
        </shared-info-message>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, onUnmounted, ref, type Ref, nextTick, type PropType, computed } from 'vue';
    import { getTimelineData } from '~/utils/dataProviders/TimelineApiDataProvider';
    import { useI18n } from 'vue-i18n';
    import { useAccountStore } from '~/stores/accountStore';
    import IconMenuHome from '~/components/icons/IconMenuHome.vue';
    import TimelineContentLoader from '~/components/timeline/TimelineContentLoader.vue';
    import type { ITimelineItem, ITimelineRequestBody, ITimelineResponseError } from '~/types/types';
    import { useNavigationStore } from '~/stores/navigationStore';
    import TimelineItem from '~/components/timeline/TimelineItem.vue';
    import { formatDate } from '~/utils/helpers';
    import { ETimelineFilterType, ETimelineIncludeType } from '~/types/enums';
    import SharedInfoMessage from '~/components/shared/SharedInfoMessage.vue';

    const props = defineProps({
        isGroupPostsPage: {
            type: Boolean,
            required: false,
            default: false,
        },
        groupIds: {
            type: Array as PropType<number[] | null>,
            required: false,
            default: () => [],
        },
        postIds: {
            type: Array as PropType<string[] | null>,
            required: false,
            default: () => [],
        },
        order: {
            type: Array as PropType<string[] | null>,
            required: false,
            default: () => [],
        },
    });

    const navStore = useNavigationStore();
    const isLoadingMore = ref(false);
    const timelineError = ref('');
    const timelineItems: Ref<ITimelineItem[]> = ref([]);
    const page = ref(1);
    const perPage = ref(10);
    const end = ref(false);
    // @ts-ignore
    const requestBody: Ref<ITimelineRequestBody> = ref({
        filter: {
            types: [
                ETimelineFilterType.NEWS,
                ETimelineFilterType.RSS,
                ETimelineFilterType.ZMAPUJTO,
                ETimelineFilterType.CALENDAR_EVENT
            ],
        },
        include: [ETimelineIncludeType.MR_CITY, ETimelineIncludeType.FILES, ETimelineIncludeType.IMAGES],
        order: props.order,
        page,
        perPage
    });

    const { appColor, buttonTextColor } = useAccountStore().state;
    const trans = useI18n().t;

    const isSinglePost = computed(() => {
        return props.postIds && props.postIds.length === 1;
    });

    const getTimelineItems = async (): Promise<void> => {
        if (page.value > 1) {
            isLoadingMore.value = true;
        }

        if (!isLoadingMore.value) {
            navStore.setLoading(true);
        }

        timelineError.value = '';
        let data: ITimelineItem[] = [];

        try {
            formatFilter();
            data = await getTimelineData(
                useAccountStore().state.locale,
                requestBody.value,
            );
        } catch (e: unknown) {
            // https://stackoverflow.com/questions/69021040/why-catch-clause-variable-type-annotation-must-be-any
            const knownError = e as ITimelineResponseError;
            if (knownError.statusCode === 500) {
                timelineError.value = trans('timeline.api_error');
            }
        }

        timelineItems.value = [...timelineItems.value, ...data];
        isLoadingMore.value = false;
        navStore.setLoading(false);

        if (data.length < perPage.value) {
            end.value = true;
        }
        if (!data.length) {
            timelineError.value = trans('timeline.no_results');
        }
    };

    const formatFilter = (): void => {
        if (props.isGroupPostsPage && props.groupIds && props.groupIds.length > 0) {
            requestBody.value.filter.groupIds = props.groupIds;
        }
        if (!props.isGroupPostsPage) {
            requestBody.value.filter.cityId = [useAccountStore().state.appCityId];
        }
        if (props.postIds && props.postIds.length > 0) {
            requestBody.value.filter.ids = props.postIds;
            requestBody.value.includeExpiredPosts = true;
            requestBody.value.includeExcludedTimelineGroupPosts = true;
        }
        if (navStore.showArchive) {
            requestBody.value.includeExpiredPosts = true;
            requestBody.value.filter.publishToTo = formatDate((new Date()).toISOString(), 'YYYY-MM-DD HH:mm:ss');
        }
    };

    const handleScrolling = (): void => {
        if ((window.innerHeight * 1.2 + window.scrollY) >= document.body.scrollHeight) {
            fetchMore();
        }
    };

    const fetchMore = (): void => {
        if (end.value || navStore.isLoading || isLoadingMore.value === true) {
            return;
        }

        page.value++;
        getTimelineItems();
    };

    onMounted( () => {
        window.addEventListener('scroll', handleScrolling);
        nextTick(() => {
            getTimelineItems();
        });
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScrolling);
    });
</script>

<style lang="scss">
    $appColor: v-bind(appColor);
    $buttonTextColor: v-bind(buttonTextColor);

    .timeline-item {
        overflow-wrap: anywhere;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.07);
        transition: all 0.15s ease;

        &:hover {
            box-shadow: 0 1px 20px 0 rgba(0,0,0,.1);
        }
    }

    .to-all-posts {
        transition: all 0.15s ease;

        &:hover {
            opacity: 0.8;
        }
    }
</style>
